import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EmployeeList({ user, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    fetchEmployees();
    fetchDashboardStats();
  }, [statusFilter, departmentFilter]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (departmentFilter) params.append('department', departmentFilter);
      if (search) params.append('search', search);

      const response = await axios.get(`${API}/employees?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEmployees();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmployees();
    } catch (error) {
      alert('Failed to delete employee: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6" data-testid="employee-list-page">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Employees</h1>
          <Link
            to="/employees/new"
            className="px-6 py-3 rounded-lg text-white font-medium transition duration-200"
            style={{backgroundColor: '#2b3380'}}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#3d4aa3'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#2b3380'}
            data-testid="add-employee-button"
          >
            + Add Employee
          </Link>
        </div>

        {/* Employee Module Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Employee Management Overview"
            icon="👥"
            bgColor="#2b3380"
            stats={[
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Active employees',
                color: '#2b3380'
              },
              {
                label: 'Departments',
                value: dashboardStats.total_departments || 0,
                subtitle: 'Active depts',
                color: '#7e57c2'
              },
              {
                label: 'NAPS Active',
                value: dashboardStats.naps_active || 0,
                subtitle: 'Apprenticeship',
                color: '#00897b'
              },
              {
                label: 'NATS Active',
                value: dashboardStats.nats_active || 0,
                subtitle: 'Training',
                color: '#00acc1'
              }
            ]}
          />
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, ID..."
              className="px-4 py-2 border rounded-lg"
              style={{borderColor: '#e0e0e0'}}
              data-testid="search-input"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
              style={{borderColor: '#e0e0e0'}}
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Resigned">Resigned</option>
              <option value="Terminated">Terminated</option>
            </select>
            <input
              type="text"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              placeholder="Filter by department"
              className="px-4 py-2 border rounded-lg"
              style={{borderColor: '#e0e0e0'}}
              data-testid="department-filter"
            />
            <button
              type="submit"
              className="px-6 py-2 rounded-lg text-white font-medium"
              style={{backgroundColor: '#2b3380'}}
              data-testid="search-button"
            >
              Search
            </button>
          </form>
        </div>

        {/* Employee List */}
        {loading ? (
          <div className="text-center py-10">Loading employees...</div>
        ) : employees.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-10 text-center">
            <p className="text-gray-500 mb-4">No employees found</p>
            <Link
              to="/employees/new"
              className="inline-block px-6 py-3 rounded-lg text-white font-medium"
              style={{backgroundColor: '#2b3380'}}
            >
              Add First Employee
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead style={{backgroundColor: '#f5f7fa'}}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#546e7a'}}>Employee ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#546e7a'}}>Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#546e7a'}}>Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#546e7a'}}>Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#546e7a'}}>Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#546e7a'}}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{borderColor: '#e0e0e0'}}>
                {employees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{color: '#2b3380'}}>
                      {employee.employee_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium" style={{color: '#2c3e50'}}>
                        {employee.first_name} {employee.last_name}
                      </div>
                      <div className="text-sm" style={{color: '#546e7a'}}>{employee.official_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{color: '#546e7a'}}>
                      {employee.designation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{color: '#546e7a'}}>
                      {employee.departments?.join(', ') || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="px-2 py-1 text-xs font-semibold rounded-full"
                        style={{
                          backgroundColor: employee.employee_status === 'Active' ? '#4caf50' : '#ff9800',
                          color: 'white'
                        }}
                      >
                        {employee.employee_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <Link
                        to={`/employees/${employee.id}`}
                        className="text-[#2b3380] hover:underline"
                        data-testid={`view-employee-${employee.id}`}
                      >
                        View
                      </Link>
                      <Link
                        to={`/employees/${employee.id}/edit`}
                        className="text-green-600 hover:underline"
                        data-testid={`edit-employee-${employee.id}`}
                      >
                        Edit
                      </Link>
                      {user.role === 'Admin' && (
                        <button
                          onClick={() => handleDelete(employee.id)}
                          className="text-red-600 hover:underline"
                          data-testid={`delete-employee-${employee.id}`}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default EmployeeList;
